import type { Meeting, Group } from '@penticton-dev-events/shared';
import { brand, platformConfig } from '@penticton-dev-events/shared';

export interface OrganizationSchema {
  '@context': string;
  '@type': string;
  '@id'?: string;
  name: string;
  url: string;
  logo?: string;
  description?: string;
  foundingDate?: string;
  email?: string;
  address?: {
    '@type': string;
    addressLocality: string;
    addressRegion: string;
    addressCountry: string;
  };
  contactPoint?: {
    '@type': string;
    contactType: string;
    url: string;
    telephone?: string;
    email?: string;
  };
  sameAs?: string[];
  knowsAbout?: string[];
}

export interface WebSiteSchema {
  '@context': string;
  '@type': string;
  name: string;
  url: string;
  description?: string;
  potentialAction?: {
    '@type': string;
    target: string | { '@type': string; urlTemplate: string };
    'query-input': string;
  };
}

export interface EventSchema {
  '@context': string;
  '@type': string;
  name: string;
  description?: string;
  startDate: string;
  endDate?: string;
  eventStatus: string;
  eventAttendanceMode: string;
  location:
    | {
        '@type': string;
        name: string;
        address?: {
          '@type': string;
          streetAddress?: string;
          addressLocality: string;
          addressRegion: string;
          addressCountry: string;
        };
      }
    | {
        '@type': string;
        url: string;
      };
  organizer: {
    '@type': string;
    name: string;
    url?: string;
  };
  offers?: {
    '@type': string;
    price: string;
    priceCurrency: string;
    availability: string;
    url: string;
  };
}

/**
 * Generate structured data for the organization using brand configuration
 */
export const generateOrganizationSchema = (): OrganizationSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${platformConfig.baseUrl}/#organization`,
  name: brand.organizer.name,
  description:
    'A community-driven organization connecting developers and tech enthusiasts in Penticton, BC through local meetups, workshops, and networking events.',
  url: platformConfig.baseUrl,
  logo: brand.assets.logo.fallback,
  email: brand.contact.email,
  address: {
    '@type': 'PostalAddress',
    addressLocality: brand.location.city,
    addressRegion: brand.location.province,
    addressCountry: brand.location.country,
  },
  sameAs: [brand.social.github.url, brand.social.linkedin.url],
  foundingDate: '2024-01-01',
  knowsAbout: [
    'Software Development',
    'Web Development',
    'Community Building',
    'Developer Networking',
    'Tech Education',
  ],
});

/**
 * Generate structured data for the website using brand configuration
 */
export const generateWebSiteSchema = (): WebSiteSchema => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: platformConfig.name,
  url: platformConfig.baseUrl,
  description: platformConfig.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${platformConfig.baseUrl}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

/**
 * Generate structured data for an event
 */
export const generateEventSchema = (meeting: Meeting, group: Group): EventSchema => {
  const isVirtual = !meeting.location && meeting.onlineUrl;

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: meeting.title,
    description: meeting.description,
    startDate: meeting.startsAt,
    endDate: meeting.endsAt,
    eventStatus:
      meeting.status === 'canceled'
        ? 'https://schema.org/EventCancelled'
        : 'https://schema.org/EventScheduled',
    eventAttendanceMode: isVirtual
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
    location: isVirtual
      ? {
          '@type': 'VirtualLocation',
          url: meeting.onlineUrl || platformConfig.baseUrl,
        }
      : {
          '@type': 'Place',
          name: meeting.location?.label || group.name,
          address: {
            '@type': 'PostalAddress',
            streetAddress: meeting.location?.address,
            addressLocality: 'Penticton',
            addressRegion: 'British Columbia',
            addressCountry: 'CA',
          },
        },
    organizer: {
      '@type': 'Organization',
      name: platformConfig.name,
      url: `${platformConfig.baseUrl}/groups/${group.id}`,
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'CAD',
      availability: 'https://schema.org/InStock',
      url: `${platformConfig.baseUrl}/meetings/${meeting.id}`,
    },
  };
};

/**
 * Generate breadcrumb structured data
 */
export const generateBreadcrumbSchema = (
  breadcrumbs: Array<{ name: string; url: string }>,
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: crumb.url,
  })),
});

/**
 * Generate FAQ structured data
 */
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});
